import React, { useEffect, useMemo, useState } from "react";
import { walkaroundTemplates } from "../data/walkaroundAudits";
import { auditForms } from "../data/auditForms";
import { auditApi } from "../services/api";

interface PlanOverride {
  owner: string;
  due: string;
  status: string;
  closedNote: string;
}

interface ManualPlan {
  id: string;
  title: string;
  detail: string;
  source: string;
  createdAt: string;
}

interface Plan {
  id: string;
  source: string;
  title: string;
  detail: string;
  createdAt: string;
  owner: string;
  due: string;
  status: string;
  closedNote: string;
}

const OVERRIDE_KEY = "action_plan_overrides";
const MANUAL_KEY = "action_plans_manual";

function loadJSON(key: string): any[] {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch (error) {
    return [];
  }
}

function loadMap(key: string): Record<string, PlanOverride> {
  try {
    return JSON.parse(localStorage.getItem(key) || "{}");
  } catch (error) {
    return {};
  }
}

const labelMaps: Record<string, string> = {};
walkaroundTemplates.forEach(t => t.sections.forEach(s => s.items.forEach(i => { labelMaps[i.id] = i.label; })));
auditForms.forEach(f => f.sections.forEach(s => s.items.forEach(i => { labelMaps[i.id] = i.label; })));

function buildDerived(): Plan[] {
  const plans: Plan[] = [];

  const pushFail = (record: any, sourceName: string, itemId: string, item: any, label: string) => {
    plans.push({
      id: `${record.id}|${itemId}`,
      source: sourceName,
      title: label,
      detail: item && item.note ? item.note : "",
      createdAt: record.completedAt || record.savedAt || "",
      owner: "",
      due: "",
      status: "open",
      closedNote: "",
    });
  };

  loadJSON("walkaround_records").forEach((record: any) => {
    const items = record.items || {};

    Object.keys(items).forEach(itemId => {
      const item = items[itemId];

      if (item && item.status === "fail") {
        pushFail(record, record.templateName || "Walkaround", itemId, item, labelMaps[itemId] || itemId);
      }
    });
  });

  loadJSON("audit_form_records").forEach((record: any) => {
    const items = record.items || {};

    Object.keys(items).forEach(itemId => {
      const item = items[itemId];

      if (item && item.status === "fail") {
        pushFail(record, record.formName || "Audit", itemId, item, labelMaps[itemId] || itemId);
      }
    });
  });

  loadJSON("med_check_records").forEach((record: any) => {
    const items = Array.isArray(record.items) ? record.items : [];

    items.forEach((item: any) => {
      if (item && item.status === "fail") {
        pushFail(record, "Medication Check", item.id, item, item.label || item.id);
      }
    });
  });
  loadJSON("mca_check_records").forEach((record: any) => {
    const items = Array.isArray(record.items) ? record.items : [];

    items.forEach((item: any) => {
      if (item && item.status === "fail") {
        pushFail(record, record.period || "MCA Check", item.id, item, item.label || item.id);
      }
    });
  });
  loadJSON("handover_records").forEach((record: any) => {
    const tasks = Array.isArray(record.tasks) ? record.tasks : [];

    tasks.forEach((task: any, index: number) => {
      if (task && task.task && !task.done) {
        plans.push({
          id: `${record.id}|task-${index}`,
          source: `Handover ${record.shift} ${record.date}`,
          title: task.task,
          detail: "Handed over task not completed",
          createdAt: record.savedAt || "",
          owner: "",
          due: "",
          status: "open",
          closedNote: "",
        });
      }
    });
  });

  return plans;
}
export default function ActionPlans({ user }: { user?: any }) {
  const [overrides, setOverrides] = useState<Record<string, PlanOverride>>(() => loadMap(OVERRIDE_KEY));
  const [manual, setManual] = useState<ManualPlan[]>(() => loadJSON(MANUAL_KEY));
  const [server, setServer] = useState<Plan[]>([]);
  const [filter, setFilter] = useState("open");
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const rows: any = await auditApi.actionPlans();
        const list = Array.isArray(rows) ? rows : [];

        setServer(list.map((row: any) => ({
          id: `server-${row.id}`,
          source: "Server",
          title: row.title || row.description || `Action ${row.id}`,
          detail: row.detail || "",
          createdAt: row.created_at || "",
          owner: row.owner || "",
          due: row.due || "",
          status: row.status || "open",
          closedNote: "",
        })));
      } catch (error) {
        setServer([]);
      }
    })();
  }, []);

  const all = useMemo(() => {
    const derived = buildDerived().map(plan => ({ ...plan, ...(overrides[plan.id] || {}) }));
    const manualPlans = manual.map(m => ({ ...m, owner: "", due: "", status: "open", closedNote: "", ...(overrides[m.id] || {}) }));

    return [...derived, ...manualPlans, ...server];
  }, [overrides, manual, server]);

  const shown = all.filter(plan => (filter === "all" ? true : plan.status === filter));

  const setPlan = (id: string, patch: Partial<PlanOverride>) => {
    const next = {
      ...overrides,
      [id]: { owner: "", due: "", status: "open", closedNote: "", ...(overrides[id] || {}), ...patch },
    };

    setOverrides(next);
    localStorage.setItem(OVERRIDE_KEY, JSON.stringify(next));
  };

  const addManual = () => {
    if (!title) return;

    const plan: ManualPlan = { id: `manual-${Date.now()}`, title, detail, source: "Manual", createdAt: new Date().toISOString() };
    const next = [plan, ...manual];

    setManual(next);
    localStorage.setItem(MANUAL_KEY, JSON.stringify(next));
    setTitle("");
    setDetail("");
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Action Plans</h2>
        <p className="text-sm text-slate-600">Every failed audit item, walkaround item, medication check and unfinished handover task becomes an action plan automatically.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {["open", "in-progress", "closed", "all"].map(status => (
          <button key={status} onClick={() => setFilter(status)} className={filter === status ? "rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"}>
            {status === "all" ? `All (${all.length})` : status}
          </button>
        ))}
      </div>

      <div className="max-w-xl space-y-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="font-semibold text-slate-900">Add manual action</h3>
        <input value={title} onChange={event => setTitle(event.target.value)} placeholder="What needs to be done" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <input value={detail} onChange={event => setDetail(event.target.value)} placeholder="Details" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        <button onClick={addManual} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Add action</button>
      </div>

      <div className="space-y-3">
        {shown.map(plan => (
          <div key={plan.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-medium text-slate-900">{plan.title}</div>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{plan.source}</span>
            </div>
            {plan.detail ? <div className="mt-1 text-sm text-slate-600">{plan.detail}</div> : null}
            {plan.createdAt ? <div className="mt-1 text-xs text-slate-500">Raised {new Date(plan.createdAt).toLocaleString()}</div> : null}

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input value={plan.owner} onChange={event => setPlan(plan.id, { owner: event.target.value })} placeholder="Owner" className="w-40 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input type="date" value={plan.due} onChange={event => setPlan(plan.id, { due: event.target.value })} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <button onClick={() => setPlan(plan.id, { status: "open" })} className={plan.status === "open" ? "rounded-lg bg-red-700 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm"}>Open</button>
              <button onClick={() => setPlan(plan.id, { status: "in-progress" })} className={plan.status === "in-progress" ? "rounded-lg bg-amber-600 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm"}>In progress</button>
              <button onClick={() => setPlan(plan.id, { status: "closed" })} className={plan.status === "closed" ? "rounded-lg bg-green-700 px-3 py-1 text-sm text-white" : "rounded-lg border border-slate-300 px-3 py-1 text-sm"}>Closed</button>
            </div>

            {plan.status === "closed" ? (
              <input value={plan.closedNote} onChange={event => setPlan(plan.id, { closedNote: event.target.value })} placeholder="Closure evidence or note" className="mt-2 w-full rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            ) : null}
          </div>
        ))}
        {shown.length === 0 ? <div className="text-sm text-slate-500">No action plans in this view.</div> : null}
      </div>
    </div>
  );
}